import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  History,
  ArrowLeft,
  FileText,
  RefreshCw,
  Eye,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import { UploadTrackerItem } from "../data/interfaces/QuestionBankInterfaces";
import Loader from "../../../../app/shared/components/Loader";
import { formatToLocalDateString } from "../../../../app/shared/utils/dateUtils";
import "../styles/QuestionBankStyles.css";

const QuestionUploadViewPage: React.FC = () => {
  const navigate = useNavigate();
  const [trackers, setTrackers] = useState<UploadTrackerItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  // File Questions Inspection Modal State
  const [selectedFile, setSelectedFile] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [fileQuestions, setFileQuestions] = useState<any[]>([]);
  const [fileModalPage, setFileModalPage] = useState<number>(1);
  const [fileModalTotalPages, setFileModalTotalPages] = useState<number>(1);
  const [fileSearchQuery, setFileSearchQuery] = useState<string>("");
  const [modalLoading, setModalLoading] = useState<boolean>(false);

  const fetchTrackers = async (p = page, l = limit) => {
    setLoading(true);
    try {
      const res = await QuestionBankClient.getUploadStatusView(undefined, p, l);
      if (res && res.trackers) {
        setTrackers(res.trackers);
        const total = res.total || res.trackers.length || 0;
        setTotalRecords(total);
        setTotalPages(Math.max(1, Math.ceil(total / l)));
      } else {
        setTrackers([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching upload history:", err);
      setTrackers([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrackers(page, limit);
  }, [page, limit]);

  const handleOpenQuestionsModal = async (
    fileId?: string,
    fileName?: string,
    p = 1,
    search = "",
  ) => {
    if (!fileId) return;
    setSelectedFile({ id: fileId, name: fileName || fileId });
    if (p === 1 && search === "") {
      setFileSearchQuery("");
    }
    setModalLoading(true);
    try {
      const res = await QuestionBankClient.getFileUploadQuestions(
        fileId,
        p,
        10,
        search,
      );
      setFileQuestions(res.questions || []);
      setFileModalTotalPages(Math.ceil((res.total || 0) / 10) || 1);
      setFileModalPage(p);
    } catch (err) {
      console.error("Error fetching file questions:", err);
      setFileQuestions([]);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="qb-container">
      <Loader isLoading={loading} message="Loading Upload History..." />

      {/* Header (Clean background without white bar card) */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", padding: "4px 4px" }}>
        <div>
          <h1 className="qb-title">
            <History size={26} /> Bulk Question Upload History
          </h1>
          <p className="qb-subtitle">
            View status tracking and processing details for past uploaded
            question files.
          </p>
        </div>
        <div className="qb-header-actions">
          <button
            className="qb-btn qb-btn-outline"
            onClick={() => fetchTrackers(page, limit)}
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            className="qb-btn qb-btn-secondary"
            onClick={() => navigate("/dashboard/question-bank/add", { state: { tab: "upload" } })}
          >
            <ArrowLeft size={16} /> Back to Bulk Upload
          </button>
        </div>
      </div>

      {/* History Table */}
      <div className="qb-table-card">
        {trackers.length === 0 ? (
          <div
            style={{ padding: "48px", textAlign: "center", color: "#64748b" }}
          >
            <FileText
              size={48}
              style={{ opacity: 0.3, marginBottom: "12px" }}
            />
            <h3>No Upload History Found</h3>
            <p>
              Uploaded Excel question files will appear here along with
              row-level validation reports.
            </p>
          </div>
        ) : (
          <>
            <div className="qb-table-scroll">
              <table className="qb-table">
                <thead>
                  <tr>
                    <th style={{ width: "60px", textAlign: "center" }}>Q.no</th>
                    <th>File Name</th>
                    <th>Type</th>
                    <th>Upload Date</th>
                    {/* <th>Total</th>
                    <th>Success</th>
                    <th>Duplicates</th> */}
                    <th>Status</th>
                    {/* <th>Details</th> */}
                    <th style={{ textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {trackers.map((t, idx) => (
                    <tr key={t.file_id || idx}>
                      <td style={{ textAlign: "center", fontWeight: 600, color: "#64748b", fontSize: "13px" }}>
                        {(page - 1) * limit + idx + 1}
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: "600",
                          }}
                        >
                          <FileText size={16} color="#0d6e5b" />
                          {t.filename || t.file_name || "Question File"}
                        </div>
                      </td>
                      <td>
                        <span className="qb-badge qb-badge-type">
                          {t.question_type || "MCQ"}
                        </span>
                      </td>
                      <td style={{ fontSize: "13px", color: "#64748b" }}>
                        {formatToLocalDateString(t.created_at || t.upload_time)}
                      </td>
                      {/* <td>
                        <strong>{t.total_questions || 0}</strong>
                      </td>
                      <td style={{ color: "#15803d", fontWeight: "600" }}>
                        {t.success_count || 0}
                      </td>
                      <td style={{ color: "#a16207" }}>
                        {t.skipped_duplicates || 0}
                      </td> */}
                      <td>
                        <span
                          className="qb-badge"
                          style={{
                            backgroundColor:
                              t.status?.includes("Successfully") ||
                              t.status?.includes("success")
                                ? "#dcfce7"
                                : "#fee2e2",
                            color:
                              t.status?.includes("Successfully") ||
                              t.status?.includes("success")
                                ? "#15803d"
                                : "#b91c1c",
                          }}
                        >
                          {t.status}
                        </span>
                      </td>
                      {/* <td style={{ fontSize: '13px', color: '#64748b', maxWidth: '240px' }}>
                        {t.status_note || 'Processed'}
                      </td> */}
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="qb-btn qb-btn-outline"
                          style={{ padding: "6px 10px" }}
                          title="View Questions in File"
                          onClick={() =>
                            handleOpenQuestionsModal(
                              t.file_id,
                              t.filename || t.file_name,
                            )
                          }
                        >
                          <Eye size={16} color="#0d6e5b" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Status Tracker Pagination - Exact QuestionListPage design */}
            {totalRecords > 0 && (
              <div className="qb-pagination">
                <div>
                  Showing {Math.min((page - 1) * limit + 1, totalRecords)} to{" "}
                  {Math.min(page * limit, totalRecords)} of {totalRecords} logs
                </div>
                <div className="qb-pagination-controls">
                  <button
                    className="qb-btn qb-btn-outline"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  >
                    Previous
                  </button>
                  <button
                    className="qb-btn qb-btn-outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* File Questions Inspection Modal */}
      {selectedFile && ReactDOM.createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(4px)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            boxSizing: "border-box",
          }}
          onClick={() => setSelectedFile(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "1100px",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#f8fafc",
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <FileText size={20} color="#0d6e5b" />
                  Questions in File: {selectedFile.name}
                </h3>
              </div>
              <button
                onClick={() => setSelectedFile(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                  padding: "4px",
                }}
              >
                <X size={22} />
              </button>
            </div>

            {/* Modal Search Bar */}
            <div
              style={{
                padding: "12px 24px",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <div style={{ position: "relative", maxWidth: "320px", width: "100%" }}>
                <Search
                  size={16}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search questions in this file..."
                  value={fileSearchQuery}
                  onChange={(e) => {
                    const newQuery = e.target.value;
                    setFileSearchQuery(newQuery);
                    if (newQuery === "") {
                      handleOpenQuestionsModal(
                        selectedFile.id,
                        selectedFile.name,
                        1,
                        "",
                      );
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleOpenQuestionsModal(
                        selectedFile.id,
                        selectedFile.name,
                        1,
                        fileSearchQuery,
                      );
                    }
                  }}
                  style={{
                    width: "100%",
                    paddingLeft: "36px",
                    paddingRight: fileSearchQuery ? "36px" : "16px",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
                {fileSearchQuery && (
                  <X
                    size={16}
                    onClick={() => {
                      setFileSearchQuery("");
                      handleOpenQuestionsModal(
                        selectedFile.id,
                        selectedFile.name,
                        1,
                        "",
                      );
                    }}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#94a3b8",
                      cursor: "pointer",
                    }}
                  />
                )}
              </div>
            </div>

            {/* Modal Content Table */}
            <div style={{ overflowY: "auto", flex: 1, padding: "0 24px 16px 24px" }}>
              {modalLoading ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: "#64748b",
                  }}
                >
                  <RefreshCw
                    size={24}
                    className="spin"
                    style={{ marginBottom: "8px" }}
                  />
                  <p>Loading file questions...</p>
                </div>
              ) : fileQuestions.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: "#94a3b8",
                  }}
                >
                  <FileText
                    size={40}
                    style={{ opacity: 0.3, marginBottom: "8px" }}
                  />
                  <p>No questions found matching your search.</p>
                </div>
              ) : (
                <table className="qb-table" style={{ marginTop: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: "60px", textAlign: "center" }}>Q.no</th>
                      <th>Question Statement</th>
                      <th>Difficulty</th>
                      <th>Option A</th>
                      <th>Option B</th>
                      <th>Option C</th>
                      <th>Option D</th>
                      <th>Answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileQuestions.map((q, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: "600", color: "#64748b" }}>
                          {(fileModalPage - 1) * 10 + idx + 1}
                        </td>
                        <td
                          style={{
                            fontWeight: "600",
                            color: "#0f172a",
                            maxWidth: "240px",
                          }}
                        >
                          {q["Question"] ||
                            q["Question Text"] ||
                            q["Title"] ||
                            q["Problem Statement"] ||
                            "-"}
                        </td>
                        <td>
                          <span className="qb-badge qb-badge-type">
                            {q["Difficulty"] || q["Difficulty Level"] || "Easy"}
                          </span>
                        </td>
                        <td style={{ color: "#475569" }}>
                          {q["Option A"] ||
                            q["OptionA"] ||
                            q["Response Option A"] ||
                            "-"}
                        </td>
                        <td style={{ color: "#475569" }}>
                          {q["Option B"] ||
                            q["OptionB"] ||
                            q["Response Option B"] ||
                            "-"}
                        </td>
                        <td style={{ color: "#475569" }}>
                          {q["Option C"] ||
                            q["OptionC"] ||
                            q["Response Option C"] ||
                            "-"}
                        </td>
                        <td style={{ color: "#475569" }}>
                          {q["Option D"] ||
                            q["OptionD"] ||
                            q["Response Option D"] ||
                            "-"}
                        </td>
                        <td style={{ color: "#0d6e5b", fontWeight: "700" }}>
                          {q["Correct Answer"] ||
                            q["Correct Option"] ||
                            q["Answer"] ||
                            "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer Controls */}
            <div
              style={{
                padding: "12px 24px",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f8fafc",
              }}
            >
              <span style={{ fontSize: "13px", color: "#64748b" }}>
                Page {fileModalPage} of {fileModalTotalPages}
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="qb-btn qb-btn-outline"
                  style={{ padding: "6px 12px", fontSize: "13px" }}
                  disabled={fileModalPage <= 1}
                  onClick={() =>
                    handleOpenQuestionsModal(
                      selectedFile.id,
                      selectedFile.name,
                      fileModalPage - 1,
                      fileSearchQuery,
                    )
                  }
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                <button
                  className="qb-btn qb-btn-outline"
                  style={{ padding: "6px 12px", fontSize: "13px" }}
                  disabled={fileModalPage >= fileModalTotalPages}
                  onClick={() =>
                    handleOpenQuestionsModal(
                      selectedFile.id,
                      selectedFile.name,
                      fileModalPage + 1,
                      fileSearchQuery,
                    )
                  }
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default QuestionUploadViewPage;
